'use client'

import { Fragment } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { useSession } from 'next-auth/react'
import { Medal, PackageX, ShoppingCart, X } from 'lucide-react'
import { useCart } from '@/lib/commerce/hooks'
import { filterDiscounts, formatImageURL, formatPrice, getEffectivePrice, getStrikethroughPrice, getLoyaltyProductDiscount, isDiscountBetter } from '@/utils/util'
import VariantOptionQuickAdd from '@/components/blocks/VariantOptionQuickAdd'
import { hasOptions } from '@/lib/commerce/variantOptions'

interface VariantSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  product: any
  vendor: any
  onAddToCart: (variantId: string) => void
}

export default function VariantSelectionModal({ isOpen, onClose, product, onAddToCart }: VariantSelectionModalProps) {
  const { data: session } = useSession()
  const { loyaltySummary, loyaltyCategoryParents } = useCart()

  const getAvailability = (variant: any) => {
    if (!product?.isTrackable) return true
    return variant.stock > 0
  }

  const getVariantImage = (variant: any) => {
    return variant?.images?.length > 0
      ? formatImageURL(variant.images[0])
      : formatImageURL('placeholder.png')
  }

  const handleSelect = (variant: any, e?: React.MouseEvent | React.TouchEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (getAvailability(variant)) {
      onAddToCart(variant.id)
      onClose()
    }
  }

  const variants = product?.variants || [product?.variant].filter(Boolean)

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70 backdrop-blur-[2px]" />
        </TransitionChild>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <TransitionChild
              as={Fragment}
              enter="transition ease-out duration-300 transform"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-[0.985]"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="transition ease-in duration-200 transform"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-[0.985]"
            >
              <DialogPanel
                className="w-full sm:max-w-lg max-h-[85vh] sm:max-h-[80vh] flex flex-col overflow-hidden rounded-t-2xl sm:rounded-3xl"
                style={{
                  background: 'linear-gradient(180deg, #16161c 0%, #0c0c10 100%)',
                  border: '1px solid rgba(255,255,255,0.13)',
                  boxShadow: '0 40px 100px -20px rgba(0,0,0,0.75)',
                }}
              >
                <div className="px-6 pt-6 pb-5 shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Төрөл сонгох</div>
                      <h3 className="mt-1 text-[16px] font-semibold leading-tight text-white truncate" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                        {product?.name}
                      </h3>
                      <p className="mt-1 text-[12px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>
                        Та хүссэн төрлөө сонгоод сагсанд нэмнэ үү
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={onClose}
                      className="h-8 w-8 grid place-items-center rounded-full transition-colors cursor-pointer shrink-0"
                      style={{ color: 'rgba(255,255,255,0.5)' }}
                      aria-label="Close"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="px-6 py-5 overflow-y-auto scrollbar-dark space-y-2">
                  {hasOptions(product) ? (
                    <VariantOptionQuickAdd product={product} tone="dark" onAdd={(variantId) => { onAddToCart(variantId); onClose() }} />
                  ) : variants.map((variant: any) => {
                    const isAvailable = getAvailability(variant)
                    const variantImage = getVariantImage(variant)
                    const vDiscounts = filterDiscounts(product?.discounts, !!session?.user?.id, variant.id)?.filter((d: any) => !(d.type === 'buyXgetY' && d.role === 'reward')) || []
                    const vEffective = getEffectivePrice(variant, vDiscounts)
                    const vStrikethrough = getStrikethroughPrice(variant, vDiscounts)
                    const vShowsDiscountBadge = vDiscounts?.length > 0 && isDiscountBetter(variant, vDiscounts)
                    const vLoyaltyDeal = getLoyaltyProductDiscount({ product, variant, summary: loyaltySummary, categoryParents: loyaltyCategoryParents, hasOtherDiscount: vShowsDiscountBadge })
                    const vDisplayEffective = vLoyaltyDeal ? Math.max(0, (vEffective ?? variant?.price ?? 0) - vLoyaltyDeal.unitSaving) : vEffective
                    const vDisplayStrike = vLoyaltyDeal ? (variant?.price ?? 0) : vStrikethrough
                    const hasVariantSale = (variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price) || (vEffective !== null && vEffective < variant.price)
                    const salePct = hasVariantSale && variant.price ? Math.round((1 - (vEffective ?? variant.priceSale ?? variant.price) / variant.price) * 100) : 0
                    return (
                      <button
                        key={variant.id}
                        type="button"
                        onClick={(e) => handleSelect(variant, e)}
                        onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleSelect(variant, e) }}
                        disabled={!isAvailable}
                        className="w-full text-left rounded-xl p-3 transition-colors cursor-pointer disabled:cursor-not-allowed"
                        style={{
                          background: 'rgba(0,0,0,0.3)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          opacity: isAvailable ? 1 : 0.5,
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative shrink-0">
                            <div
                              className="w-14 h-14 rounded-md overflow-hidden"
                              style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
                            >
                              <img src={variantImage} alt={variant.name} className="w-full h-full object-cover" />
                            </div>
                            {salePct > 0 && !vLoyaltyDeal?.saleOffset && (
                              <span
                                className="absolute -top-1 -right-1 text-[9px] font-bold tabular-nums px-1 py-0.5 rounded"
                                style={{ background: '#fda4af', color: '#0a0a0c' }}
                              >
                                −{salePct}%
                              </span>
                            )}
                            {vLoyaltyDeal && (
                              <span className="absolute -bottom-1 -right-1 inline-flex items-center gap-0.5 text-[9px] font-bold tabular-nums px-1 py-0.5 rounded bg-violet-600 text-white">
                                <Medal className="h-2.5 w-2.5" />
                                -{vLoyaltyDeal.percent}%
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13.5px] font-medium text-white truncate">{variant.name}</div>
                            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                              {vDisplayEffective !== null ? (
                                vDisplayEffective === 0 ? (
                                  <>
                                    <span className="text-[13px] font-semibold tabular-nums" style={{ color: '#34d399' }}>Үнэгүй</span>
                                    {vDisplayStrike != null && (
                                      <span className="text-[11px] tabular-nums line-through" style={{ color: 'rgba(255,255,255,0.4)' }}>{formatPrice(vDisplayStrike)}</span>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <span className="text-[13px] font-semibold tabular-nums" style={{ color: '#fda4af' }}>{formatPrice(vDisplayEffective)}</span>
                                    {vDisplayStrike != null && (
                                      <span className="text-[11px] tabular-nums line-through" style={{ color: 'rgba(255,255,255,0.4)' }}>{formatPrice(vDisplayStrike)}</span>
                                    )}
                                  </>
                                )
                              ) : (
                                <span className="text-[13px] tabular-nums text-white">{formatPrice(variant.price)}</span>
                              )}
                            </div>
                            {product?.isTrackable && (
                              <div className="mt-1 text-[11px]" style={{ color: isAvailable ? 'rgba(255,255,255,0.45)' : '#fda4af' }}>
                                {isAvailable ? `${variant.stock} ширхэг үлдсэн` : 'Дууссан'}
                              </div>
                            )}
                          </div>
                          <div className="shrink-0">
                            {isAvailable ? (
                              <div
                                className="h-9 w-9 rounded-full grid place-items-center"
                                style={{ background: 'white', color: '#0a0a0c' }}
                              >
                                <ShoppingCart className="h-4 w-4" />
                              </div>
                            ) : (
                              <div
                                className="h-9 w-9 rounded-full grid place-items-center"
                                style={{ background: 'rgba(244,63,94,0.16)', color: '#fda4af', border: '1px solid rgba(244,63,94,0.3)' }}
                              >
                                <PackageX className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>

                <div
                  className="px-6 py-4 shrink-0"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.3)' }}
                >
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full h-11 rounded-full text-[13px] font-medium cursor-pointer transition-colors"
                    style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                  >
                    Цуцлах
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
